import type { Message } from "../hooks/useChat";
import { fetchAndDecryptToDataUrl } from "./file-crypto";
import { parseFileAttachments } from "./file-tokens";

// ── Reine Helfer ────────────────────────────────────────────────────────────

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** *fett* → <strong>, _kursiv_ → <em>. NUR auf bereits escaptem Text anwenden. */
export function applyEmphasis(escaped: string): string {
  return escaped
    .replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>")
    .replace(/_([^_\n]+)_/g, "<em>$1</em>");
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "chat";
}

// ── Renderer ─────────────────────────────────────────────────────────────────

export interface ExportBaseOpts {
  channelName: string;
  currentUserName: string;
  ownUserId: string;
}
export interface RenderOpts extends ExportBaseOpts {
  imagesByMsgId: Map<string, string[]>;
  now: Date;
}

const TZ = "Europe/Berlin";
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
}
function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: TZ });
}
function fmtDateTime(d: Date): string {
  return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short", timeZone: TZ });
}

const EXPORT_CSS = `
  :root { color-scheme: dark; }
  body { margin:0; background:#0C0C0E; color:#F2F2F4;
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  .container { max-width:720px; margin:0 auto; padding:24px 16px; }
  .exportHeader { background:#131316; border-radius:12px; padding:16px; margin-bottom:16px; }
  .exportHeader h1 { font-size:18px; margin:0 0 6px; color:#F2F2F4; }
  .exportHeader .meta { font-size:12px; color:#9C9CA6; }
  .dayDivider { text-align:center; font-size:11px; color:#9C9CA6; margin:16px 0 8px; }
  .row { display:flex; margin:6px 0; gap:8px; align-items:flex-end; }
  .row.own { flex-direction:row-reverse; }
  .col { display:flex; flex-direction:column; max-width:78%; }
  .avatar { width:28px; height:28px; border-radius:50%; flex:0 0 28px;
    background:#212134; color:#E67E22; font-size:11px; font-weight:600;
    display:flex; align-items:center; justify-content:center; }
  .senderName { font-size:12px; font-weight:600; color:#E67E22; margin:0 0 2px 2px; }
  .bubble { padding:6px 12px; border-radius:18px; font-size:15px; line-height:1.4;
    white-space:pre-wrap; word-break:break-word; }
  .ownBubble { background:#6C5ECF; color:#FFFFFF; border-bottom-right-radius:4px; }
  .otherBubble { background:#212134; color:#F2F2F4; border-bottom-left-radius:4px; }
  .time { font-size:10px; opacity:0.6; margin-left:8px; float:right; }
  .deleted { font-style:italic; opacity:0.6; }
  .replyQuote { font-size:12px; color:#9C9CA6; border-left:2px solid #E67E22;
    padding-left:6px; margin:0 0 3px 2px; max-width:78%; overflow:hidden;
    text-overflow:ellipsis; white-space:nowrap; }
  .reactions { display:flex; gap:4px; margin:3px 0 0 2px; flex-wrap:wrap; }
  .reactChip { background:rgba(255,255,255,0.08); border-radius:10px; padding:1px 6px;
    font-size:11px; color:#F2F2F4; }
  .fileChip { display:inline-block; background:rgba(255,255,255,0.06);
    border:1px solid rgba(255,255,255,0.12); border-radius:8px; padding:4px 8px;
    font-size:13px; color:#F2F2F4; }
  .bubble img { max-width:100%; border-radius:8px; display:block; }
`.trim();

function renderReply(m: Message, all: Message[]): string {
  if (!m.replyToId) return "";
  const target = all.find((x) => x.id === m.replyToId);
  const stripped = target?.plaintext ? parseFileAttachments(target.plaintext).text.trim() : "";
  const label = stripped ? escapeHtml(stripped.slice(0, 80)) : "Antwort";
  return `<div class="replyQuote">↩ ${label}</div>`;
}

function renderReactions(m: Message): string {
  if (!m.reactions || m.reactions.length === 0) return "";
  const chips = m.reactions
    .map((r) => `<span class="reactChip">${escapeHtml(r.emoji)} ${r.count}</span>`)
    .join("");
  return `<div class="reactions">${chips}</div>`;
}

function renderContent(m: Message, opts: RenderOpts): string {
  if (m.deletedAt) return `<span class="deleted">[Nachricht gelöscht]</span>`;
  if (m.plaintext == null) return `<span class="deleted">[verschlüsselt — nicht geladen]</span>`;

  const { text, files } = parseFileAttachments(m.plaintext);
  let html = "";

  // Bilder liegen als data:-URIs in derselben Reihenfolge vor wie die
  // Bild-Token der Nachricht (siehe resolveInlineImages).
  const dataUris = opts.imagesByMsgId.get(m.id) ?? [];
  let imageIndex = 0;

  for (const file of files) {
    if (file.contentType.startsWith("image/")) {
      const uri = dataUris[imageIndex++];
      html += uri
        ? `<img src="${escapeHtml(uri)}" alt="${escapeHtml(file.filename)}">`
        : `<span class="fileChip">[Bild: ${escapeHtml(file.filename)}]</span>`;
    } else if (file.contentType.startsWith("audio/")) {
      html += `<span class="fileChip">[Sprachnachricht]</span>`;
    } else {
      html += `<span class="fileChip">[Datei: ${escapeHtml(file.filename)}]</span>`;
    }
  }

  const trimmed = text.trim();
  if (trimmed) {
    html += applyEmphasis(escapeHtml(text)).replace(/\n/g, "<br>");
  }
  return html || "&nbsp;";
}

function renderMessage(m: Message, opts: RenderOpts, all: Message[]): string {
  const isOwn = m.senderId === opts.ownUserId;
  const bubbleClass = isOwn ? "ownBubble" : "otherBubble";
  const time = `<span class="time">${fmtTime(m.createdAt)}${m.editedAt ? " (bearbeitet)" : ""}</span>`;
  const bubble = `<div class="bubble ${bubbleClass}">${renderContent(m, opts)}${time}</div>`;

  if (isOwn) {
    return `<div class="row own"><div class="col">${renderReply(m, all)}${bubble}${renderReactions(m)}</div></div>`;
  }
  const av = `<div class="avatar">${escapeHtml(initials(m.senderName ?? "?"))}</div>`;
  const name = m.senderName ? `<div class="senderName">${escapeHtml(m.senderName)}</div>` : "";
  return `<div class="row">${av}<div class="col">${name}${renderReply(m, all)}${bubble}${renderReactions(m)}</div></div>`;
}

export function renderConversationHtml(messages: Message[], opts: RenderOpts): string {
  const body: string[] = [];
  let lastDay = "";
  for (const m of messages) {
    const day = fmtDay(m.createdAt);
    if (day !== lastDay) { body.push(`<div class="dayDivider">${escapeHtml(day)}</div>`); lastDay = day; }
    body.push(renderMessage(m, opts, messages));
  }
  const first = messages[0];
  const header =
    `<div class="exportHeader"><h1>KollegePlus — ${escapeHtml(opts.channelName)}</h1>` +
    `<div class="meta">Export: ${escapeHtml(fmtDateTime(opts.now))} · ${messages.length} Nachrichten` +
    `${first ? " · ab " + escapeHtml(fmtDay(first.createdAt)) : ""}<br>` +
    `Exportiert von: ${escapeHtml(opts.currentUserName)} (Admin)</div></div>`;

  // AI-Act-Kennzeichnung: ein Export ist ein Output außerhalb des Chats und kann
  // von KI-Assistenten erzeugte Nachrichten enthalten — sichtbar kennzeichnen.
  const aiActFooter =
    `<div style="margin:28px auto 12px;max-width:760px;padding:10px 14px;` +
    `border-top:1px solid #2a2f3a;color:#8b93a7;font-size:12px;line-height:1.5;text-align:center">` +
    `🤖 Diese Unterhaltung kann von KI-Assistenten erzeugte Nachrichten enthalten ` +
    `(Kennzeichnung gem. EU-KI-Verordnung). KI-Ausgaben bitte fachlich prüfen.</div>`;

  return `<!DOCTYPE html>
<html lang="de"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>KollegePlus Export — ${escapeHtml(opts.channelName)}</title>
<style>${EXPORT_CSS}</style></head>
<body><div class="container">${header}${body.join("")}</div>${aiActFooter}</body></html>`;
}

// ── Bild-Auflösung + Orchestrierung ──────────────────────────────────────────

export interface ResolveDeps {
  fetchDataUrl: (fileId: string, mime: string, key?: string, iv?: string) => Promise<string>;
}

export async function resolveInlineImages(
  messages: Message[],
  deps: ResolveDeps = { fetchDataUrl: fetchAndDecryptToDataUrl },
): Promise<Map<string, string[]>> {
  const out = new Map<string, string[]>();
  for (const m of messages) {
    if (m.plaintext == null) continue;
    const { files } = parseFileAttachments(m.plaintext);
    const uris: string[] = [];
    for (const file of files) {
      if (!file.contentType.startsWith("image/")) continue;
      try {
        uris.push(await deps.fetchDataUrl(file.fileId, file.contentType, file.encryptionKey, file.encryptionIv));
      } catch {
        // Platzhalter, damit die Reihenfolge zu den Bild-Token passt;
        // der Render fällt für diesen Eintrag auf [Bild: name] zurück.
        uris.push("");
      }
    }
    if (uris.some(Boolean)) out.set(m.id, uris);
  }
  return out;
}

export async function exportConversationAsHtml(
  messages: Message[],
  opts: ExportBaseOpts,
): Promise<void> {
  const now = new Date();
  const imagesByMsgId = await resolveInlineImages(messages);
  const html = renderConversationHtml(messages, { ...opts, imagesByMsgId, now });

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const date = now.toISOString().slice(0, 10);
  a.download = `kollegeplus-${slugify(opts.channelName)}-${date}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
